(() => {
  function init(scope = document) {
    scope.querySelectorAll('[data-home-carousel]').forEach(carousel => {
      if (carousel.dataset.initialized) return;
      carousel.dataset.initialized = 'true';
      const large = carousel.classList.contains('shopde-feature-carousel--large');
      const mobile = matchMedia('(max-width: 989px)');
      const mobileRail = carousel.querySelector('[data-home-mobile-rail]');
      const desktopRail = carousel.querySelector('[data-home-desktop-rail]');
      const fixedRail = carousel.querySelector('[data-home-rail]');
      const prev = carousel.querySelector('[data-home-prev]');
      const next = carousel.querySelector('[data-home-next]');
      const dots = carousel.querySelector('.shopde-feature-dots');
      const homeHeader = large && document.body.classList.contains('shopde-homepage');
      let headerCard;
      const resetHeader = () => {
        if (!homeHeader) return;
        document.body.classList.remove('shopde-feature-header');
        document.body.style.removeProperty('--home-header-background');
        document.body.style.removeProperty('--home-header-foreground');
        headerCard = null;
      };
      const updateHeader = card => {
        if (!homeHeader) return;
        if (!mobile.matches || !card) {
          resetHeader();
          return;
        }
        if (headerCard === card) return;
        const colors = getComputedStyle(card);
        const background = colors.getPropertyValue('--home-header-background').trim();
        const foreground = colors.getPropertyValue('--home-header-foreground').trim();
        if (!background || !foreground) {
          resetHeader();
          return;
        }
        document.body.style.setProperty('--home-header-background', background);
        document.body.style.setProperty('--home-header-foreground', foreground);
        document.body.classList.add('shopde-feature-header');
        headerCard = card;
      };
      let rail;
      const cards = () => large && mobile.matches
        ? [...carousel.querySelectorAll('.shopde-feature-hero, .shopde-feature-card')]
        : [...rail.children];
      const cardPosition = card => {
        const bounds = card.getBoundingClientRect();
        const position = bounds.left - rail.getBoundingClientRect().left + rail.scrollLeft;
        return position - (large && mobile.matches ? (rail.clientWidth - bounds.width) / 2 : 0);
      };
      const currentCard = items => items.reduce((best, card, index) =>
        Math.abs(cardPosition(card) - rail.scrollLeft) < Math.abs(cardPosition(items[best]) - rail.scrollLeft) ? index : best, 0);
      // Keep the large mobile rail with its own history entry; native page scroll stays untouched.
      const historyKey = 'shopdeHomeCarousel';
      const savedPosition = () => {
        if (!homeHeader || !mobile.matches) return null;
        try {
          const saved = window.history.state?.[historyKey];
          return saved?.rail === mobileRail.id && Number.isInteger(saved.index) &&
            saved.index >= 0 && saved.index < cards().length ? saved : null;
        } catch {
          return null;
        }
      };
      const rememberPosition = () => {
        if (!homeHeader || !mobile.matches || rail !== mobileRail) return;
        try {
          const state = window.history.state;
          if (state !== null && Object.prototype.toString.call(state) !== '[object Object]') return;
          const saved = { rail: rail.id, index: currentCard(cards()), left: rail.scrollLeft, width: rail.clientWidth };
          if (JSON.stringify(state?.[historyKey]) === JSON.stringify(saved)) return;
          window.history.replaceState({ ...state, [historyKey]: saved }, '');
        } catch {
          // Storage restrictions must never interrupt a product link or the carousel.
        }
      };
      const rememberLink = event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href]');
        if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
        if (link.origin === window.location.origin) rememberPosition();
      };
      const update = () => {
        prev.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
        prev.hidden = prev.disabled;
        next.hidden = next.disabled;
        if (dots && mobile.matches) {
          const items = cards();
          const current = currentCard(items);
          [...dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === current));
          updateHeader(items[current]);
        } else {
          resetHeader();
        }
      };
      const step = direction => {
        const items = cards();
        if (large && mobile.matches) {
          const current = currentCard(items);
          const target = items[Math.max(0, Math.min(items.length - 1, current + direction))];
          if (target) rail.scrollTo({ left: cardPosition(target), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        } else {
          rail.scrollBy({ left: direction * rail.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
      };
      const keydown = event => {
        if (event.target !== rail || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        step(event.key === 'ArrowRight' ? 1 : -1);
      };
      const resize = new ResizeObserver(update);
      const restorePosition = () => {
        const saved = savedPosition();
        if (!saved) return;
        const left = Number.isFinite(saved.left) && Math.abs(saved.width - rail.clientWidth) < 1
          ? saved.left : cardPosition(cards()[saved.index]);
        rail.scrollTo({ left, behavior: 'instant' });
        update();
      };
      const historyReturn = performance.getEntriesByType('navigation')[0]?.type === 'back_forward';
      let firstSelection = true;
      let restoreFrame;
      const restoreOnShow = event => {
        if (!event.persisted && !historyReturn) return;
        cancelAnimationFrame(restoreFrame);
        // Run after the browser has restored its own persisted scroll positions.
        restoreFrame = requestAnimationFrame(restorePosition);
      };
      const selectRail = () => {
        if (rail) {
          rail.removeEventListener('scroll', update);
          rail.removeEventListener('keydown', keydown);
          resize.unobserve(rail);
        }
        rail = large ? (mobile.matches ? mobileRail : desktopRail) : fixedRail;
        if (large) {
          mobileRail.tabIndex = mobile.matches ? 0 : -1;
          desktopRail.tabIndex = mobile.matches ? -1 : 0;
        }
        prev.setAttribute('aria-controls', rail.id);
        next.setAttribute('aria-controls', rail.id);
        rail.addEventListener('scroll', update, { passive: true });
        rail.addEventListener('keydown', keydown);
        resize.observe(rail);
        rail.scrollTo({ left: 0, behavior: 'instant' });
        if (firstSelection && historyReturn) restorePosition();
        firstSelection = false;
        update();
      };
      const previous = () => step(-1);
      const following = () => step(1);
      prev.addEventListener('click', previous);
      next.addEventListener('click', following);
      mobile.addEventListener('change', selectRail);
      if (homeHeader) {
        carousel.addEventListener('click', rememberLink, true);
        window.addEventListener('pagehide', rememberPosition);
        window.addEventListener('pageshow', restoreOnShow);
      }
      carousel._homeCleanup = () => {
        mobile.removeEventListener('change', selectRail);
        rail.removeEventListener('scroll', update);
        rail.removeEventListener('keydown', keydown);
        prev.removeEventListener('click', previous);
        next.removeEventListener('click', following);
        carousel.removeEventListener('click', rememberLink, true);
        window.removeEventListener('pagehide', rememberPosition);
        window.removeEventListener('pageshow', restoreOnShow);
        cancelAnimationFrame(restoreFrame);
        resize.disconnect();
        resetHeader();
      };
      selectRail();
    });
  }
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-home-carousel]').forEach(el => el._homeCleanup?.());
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();


